import { Component, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { takeUntil } from 'rxjs/operators';
import { ConfirmationComponent } from '../../../shared/confirmation/confirmation.component';
import { adaptDeliveryStatus } from '../../../shared/models/enums/sms-delivery-status.enum';
import { Pagination } from '../../../shared/pagination/pagination';
import { PaginationOptions } from '../../../shared/pagination/pagination-options';
import { BeneficiariesService } from '../../../shared/services/beneficiaries.service';
import { DocumentsService } from '../../../shared/services/documents.service';
import { SortDirection, SortEvent } from '../../../shared/sortable/sort-event';
import { SortableDirective } from '../../../shared/sortable/sortable.directive';
import { ToastService } from '../../../shared/toast/toast.service';
import { BeneficiaryBaseComponent } from '../../beneficiary-base.component';
import { BeneficiaryDto } from '../../dto/beneficiary.dto';
import { DocumentDetailsComponent } from './document-details/document-details.component';
import { DocumentDto } from './document.dto';

@Component({
  selector: 'app-document',
  templateUrl: './document.component.html',
})
export class DocumentComponent extends BeneficiaryBaseComponent implements OnInit, OnDestroy {
  private static readonly resetDocuments: Pagination<DocumentDto> = new Pagination([], 0, 0, 1, new PaginationOptions(1, 25));
  @ViewChildren(SortableDirective)
  public headers: QueryList<SortableDirective>;

  public documents: Pagination<DocumentDto> = DocumentComponent.resetDocuments;

  public get smsTitleId(): number {
    return 17;
  }

  private tag: string = 'parcours';

  public adaptDeliveryStatus: (status: string) => string = adaptDeliveryStatus;

  constructor(
    private readonly documentsService: DocumentsService,
    private readonly modalService: NgbModal,
    private readonly toastService: ToastService,
    private readonly beneficiariesService: BeneficiariesService,
    private readonly route: Router,
  ) {
    super();
  }

  public async ngOnInit(): Promise<void> {
    this.tag = this.route.url.split('/').includes('rsj') ? 'rsj' : 'parcours';
    this.beneficiariesService.beneficiary.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((beneficiary: BeneficiaryDto) => (this.beneficiary = beneficiary));

    await this.updateDocuments(this.documents.options);
    this.documentsService.documentCreated$.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateDocuments(this.documents.options));
    this.documentsService.documentDeleted$.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateDocuments(this.documents.options));
  }

  public async download(document: DocumentDto): Promise<void> {
    return this.documentsService.downloadDocument(document.id, document.fileName);
  }

  public async onSort(event: SortEvent): Promise<void> {
    // resetting other headers
    const column: string = event.column;
    const direction: SortDirection = event.direction;
    this.headers.forEach((header: SortableDirective) => {
      if (header.sortable !== column) {
        header.direction = '';
      }
    });
    await this.updateDocuments(this.documents.options.withSort(column, direction == 'desc'));
  }

  public async changePage(pageNb?: number): Promise<void> {
    if (pageNb) {
      await this.updateDocuments(this.documents.options.withPage(pageNb));
    }
  }

  public async changeLimit(event: any): Promise<void> {
    if (event && event.target && event.target.value) {
      const limit: number = event.target.value;
      await this.updateDocuments(this.documents.options.withLimit(limit));
    }
  }

  public async openCreateModal(): Promise<void> {
    const modal: NgbModalRef = this.modalService.open(DocumentDetailsComponent, {
      windowClass: 'rightModal',
      size: 'lg',
      backdrop: 'static',
    });
    const modalComponent: DocumentDetailsComponent = modal.componentInstance as DocumentDetailsComponent;
    await modalComponent.onInit(this.beneficiary.id, this.tag);
  }

  public async deleteDocument(idDocument: number): Promise<void> {
    if (idDocument) {
      const confirm: boolean = await this.openConfirmationModal();
      if (confirm) {
        try {
          await this.documentsService.deleteDocument(idDocument);
          this.toastService.show('Le document a été supprimé avec succès.', 'success');
        } catch (e) {
          if (e.status === 464) {
            this.toastService.show('Suppression impossible : le RIB est lié à une information bancaire.', 'danger');
          } else if (e.status === 465) {
            this.toastService.show('Suppression impossible : le document est lié à une instruction.', 'danger');
          } else {
            this.toastService.show('Une erreur serveur est survenue, merci de réessayer plus tard.', 'danger');
          }
        }
      }
    }
  }

  private async openConfirmationModal(): Promise<boolean> {
    const modal: NgbModalRef = this.modalService.open(ConfirmationComponent, {
      backdrop: 'static',
      centered: true,
    });
    const modalComponent: ConfirmationComponent = modal.componentInstance as ConfirmationComponent;
    return await modalComponent.confirm('Êtes-vous sûr(e) de vouloir supprimer ce document ?');
  }

  private async updateDocuments(pagination: PaginationOptions): Promise<void> {
    this.documents = await this.documentsService.findByBeneficiaryId(this.beneficiary.id, this.tag, pagination);
  }
}
